import React, {useEffect, useMemo, useState} from 'react';
import Forms from "./Forms";
import Error from "./Error";
import API from "../utils/API";
import {API_ENDPOINTS} from "../apiConfig";

const DonorDiagnosticModal = ({close, buyback, onSaved}) => {
    const isLocked = Boolean(buyback?.item?.donorPartsLockedAt);
    const [rows, setRows] = useState(() => buildRows(buyback, []));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const selectedCount = useMemo(() => rows.filter((row) => row.contains).length, [rows]);
    const buybackPrice = Number(buyback?.economics?.purchasePrice);
    const partsTotal = useMemo(() => rows
        .filter((row) => row.contains)
        .reduce((sum, row) => sum + toNonNegativeNumber(row.price, 0), 0), [rows]);
    const priceDiff = Number.isFinite(buybackPrice) ? (buybackPrice - partsTotal) : 0;
    const isPriceValid = !Number.isFinite(buybackPrice) || Math.abs(priceDiff) < 0.000001;

    const updateRow = (rowId, patch) => {
        setRows((prev) => prev.map((row) => row.rowId === rowId ? {...row, ...patch} : row));
    };

    const submit = async (mode) => {
        if (!isPriceValid) {
            setError({errorMessage: `Součet cen dílů (${partsTotal}) se musí rovnat ceně výkupu (${buybackPrice}).`});
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                donorParts: rows.map((item) => ({
                    part: item.selectedPart || item.part,
                    contains: item.contains === true,
                    price: item.contains === true ? toNonNegativeNumber(item.price, 0) : 0,
                    note: String(item.note || '').trim()
                }))
            };
            if (mode === 'stock') {
                await API.put(API_ENDPOINTS.buybacks.stockDonorDiagnostics(buyback._id), payload);
            } else {
                await API.put(API_ENDPOINTS.buybacks.saveDonorDiagnostics(buyback._id), payload);
            }
            await onSaved?.();
            close?.(true);
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        let mounted = true;
        const loadParts = async () => {
            try {
                const deviceId = String(buyback?.device?._id || buyback?.device || '');
                if (!deviceId) {
                    if (mounted) setRows(buildRows(buyback, []));
                    return;
                }
                const response = await API.get(API_ENDPOINTS.part.byDevice(deviceId));
                if (!mounted) return;
                setRows(buildRows(buyback, response?.data || []));
            } catch (e) {
                if (!mounted) return;
                setRows(buildRows(buyback, []));
            }
        };
        loadParts();
        return () => {
            mounted = false;
        };
    }, [buyback]);

    return (
        <div className="space-y-4">
            <div className="text-sm text-gray-700">
                {buyback?.buybackNumber || '-'} • {buyback?.deviceSnapshot?.name || buyback?.device?.name || 'Zařízení'}
                {Number.isFinite(buybackPrice) ? ` • cena výkupu ${buybackPrice}` : ''}
                {buyback?.item?.donorPartsLockedAt ? ` • naskladněno ${new Date(buyback.item.donorPartsLockedAt).toLocaleString('cs-CZ')}` : ''}
            </div>
            <div className="border rounded overflow-hidden">
                <div className="grid gap-3 px-3 py-2 bg-gray-100 text-xs font-semibold uppercase tracking-wide" style={{gridTemplateColumns: 'minmax(80px, 1fr) 100px 80px minmax(260px, 1fr)'}}>
                    <div>Díl</div>
                    <div>Obsahuje</div>
                    <div>Cena</div>
                    <div>Poznámka</div>
                </div>
                <div className="max-h-[420px] overflow-auto">
                    {rows.map((row) => (
                        <div key={row.rowId} className="grid gap-3 items-center px-3 py-3 border-t" style={{gridTemplateColumns: 'minmax(80px, 1fr) 100px 80px minmax(260px, 1fr)'}}>
                            <div className="text-sm font-medium">
                                {(row.options || []).length > 1 ? (
                                    <Forms.Select
                                        value={(row.options || []).map((item) => ({value: item.id, label: item.name})).find((item) => item.value === row.selectedPart) || null}
                                        onChange={(option) => updateRow(row.rowId, {selectedPart: option?.value || row.part})}
                                        options={(row.options || []).map((item) => ({value: item.id, label: item.name}))}
                                        placeholder={row.partName || 'Vyberte díl'}
                                        fullWidth
                                        wrapLabel
                                        disabled={isLocked}
                                    />
                                ) : (
                                    <div className="text-sm">{row.options?.[0]?.name || row.partName || '-'}</div>
                                )}
                            </div>
                            <label className="flex items-center ml-2 text-sm text-gray-900 gap-2">
                                <input
                                    type="checkbox"
                                    checked={row.contains}
                                    disabled={isLocked}
                                    onChange={(e) => updateRow(row.rowId, {contains: e.target.checked})}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                disabled={isLocked || !row.contains}
                                value={row.contains ? row.price : 0}
                                onChange={(e) => updateRow(row.rowId, {price: e.target.value})}
                                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                            <textarea
                                value={row.note}
                                disabled={isLocked}
                                onChange={(e) => updateRow(row.rowId, {note: e.target.value})}
                                placeholder="Poznámka technika"
                                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div className="text-sm text-gray-700">
                Označeno k naskladnění: <strong>{selectedCount}</strong> • Součet cen dílů: <strong>{partsTotal}</strong>
                {Number.isFinite(buybackPrice) ? <> • Rozdíl: <strong className={isPriceValid ? 'text-emerald-700' : 'text-red-700'}>{priceDiff}</strong></> : null}
            </div>
            {error ? <Error error={error}/> : null}
            <div className="flex justify-end gap-3">
                {!isLocked ? <Forms.Button label={saving ? 'Ukládám…' : 'Uložit'} disabled={saving} onClick={() => submit('save')}/> : null}
                {!isLocked ? <Forms.Button className="bg-emerald-700 hover:bg-emerald-800" label={saving ? 'Naskladňuji…' : 'Naskladnit'} disabled={saving} onClick={() => submit('stock')}/> : null}
                <Forms.Button className="bg-gray-600 hover:bg-gray-700" label="Zavřít" onClick={() => close?.()}/>
            </div>
        </div>
    );
};

function buildRows(buyback, catalogRows = []) {
    const fromApi = Array.isArray(catalogRows)
        ? catalogRows.map((row) => normalizeCatalogPartRow(row)).filter(Boolean)
        : [];
    const existingMap = new Map((buyback?.item?.donorParts || []).map((item) => [String(item?.part?._id || item?.part), item]));

    return fromApi.map((item) => {
        const existing = (item.options || [])
            .map((option) => existingMap.get(option.id))
            .find(Boolean) || existingMap.get(item.part);
        return {
            rowId: item.rowId || item.part,
            part: item.part,
            partName: resolveName(item.part, item.partName || existing?.part?.name),
            options: (item.options || [{id: item.part, name: item.partName || existing?.part?.name || ''}]).map((option) => ({
                id: option.id,
                name: resolveName(option.id, option.name)
            })),
            selectedPart: String(existing?.part?._id || existing?.part || item.part),
            contains: existing?.contains === true,
            price: existing?.contains === true ? toNonNegativeNumber(existing?.price, 0) : 0,
            note: String(existing?.note || '')
        };
    });
}

function normalizeCatalogPartRow(entry) {
    const parameterLabelMap = buildParameterLabelMap(entry);

    const variants = Array.isArray(entry?.variants) ? entry.variants : [];
    const options = variants
        .map((variant) => ({
            id: String(variant?._id || ''),
            name: formatVariantOptionLabel(entry, variant, parameterLabelMap)
        }))
        .filter((item) => item.id);
    if (!options.length) return null;

    const masterName = String(entry?.name || '').trim();
    const partName = masterName || options[0]?.name || 'Díl';
    const masterId = String(entry?._id || '').trim();

    return {
        rowId: masterId ? `part_${masterId}` : `part_${options.map((item) => item.id).join('_')}`,
        part: options[0].id,
        partName,
        options
    };
}

function buildParameterLabelMap(entry) {
    const parameterLabelMap = new Map();
    const schemaRows = [
        ...(Array.isArray(entry?.parameterSchema) ? entry.parameterSchema : []),
        ...(Array.isArray(entry?.variantSchema) ? entry.variantSchema : []),
        ...(Array.isArray(entry?.part?.variantSchema) ? entry.part.variantSchema : [])
    ];

    schemaRows.forEach((item) => {
        const parameter = item?.parameter && typeof item.parameter === 'object' ? item.parameter : {};
        const key = String(item?.key || parameter?.key || '').trim();
        const parameterId = String(item?.parameterId || parameter?._id || item?.parameter || '').trim();
        const label = String(item?.label || parameter?.label || key || parameterId).trim();
        if (key) parameterLabelMap.set(key, label || key);
        if (parameterId) parameterLabelMap.set(parameterId, label || parameterId);
    });

    return parameterLabelMap;
}

function formatVariantOptionLabel(master, variant, parameterLabelMap) {
    const masterName = String(master?.name || '').trim() || 'Díl';
    const attributes = variant?.attributes && typeof variant.attributes === 'object' ? variant.attributes : {};
    const attributePairs = Object.entries(attributes)
        .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
        .map(([key, value]) => {
            const label = parameterLabelMap.get(String(key)) || String(key);
            return `${label}: ${formatAttributeValue(value)}`;
        });

    if (!attributePairs.length) {
        return masterName;
    }
    return `${masterName} (${attributePairs.join(', ')})`;
}

function formatAttributeValue(value) {
    if (Array.isArray(value)) {
        return value.map((item) => String(item)).join(', ');
    }
    if (typeof value === 'boolean') {
        return value ? 'Ano' : 'Ne';
    }
    return String(value);
}

function resolveName(partId, preferredName) {
    const preferred = String(preferredName || '').trim();
    if (preferred) return preferred;
    return `Díl ${String(partId)}`;
}

function toNonNegativeNumber(value, fallback = 0) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return fallback;
    return parsed;
}

export default DonorDiagnosticModal;
